import React, { useState } from "react";

import { ConfirmationModal } from "@/components/ui";
import { type FolderFlag, setAllPlan } from "@/lib/leaves/leafRules";

interface Flagged {
  alwaysRequired: boolean;
  perStore: boolean;
  usedBy: number;
}

interface Target<T extends Flagged> {
  folder: string;
  flag: FolderFlag;
  leaves: T[];
}

interface FolderSetAllConfirmProps<T extends Flagged> {
  target: Target<T> | null;
  onClose: () => void;
  onConfirm: (leaves: T[], flag: FolderFlag, next: boolean) => void;
}

const FLAG_NAME: Record<FolderFlag, string> = {
  alwaysRequired: "Required",
  perStore: "Per store",
};

function Body<T extends Flagged>({
  target: { folder, flag, leaves },
  onClose,
  onConfirm,
}: Omit<FolderSetAllConfirmProps<T>, "target"> & { target: Target<T> }) {
  const on = setAllPlan(leaves, flag, true);
  const off = setAllPlan(leaves, flag, false);
  const [next, setNext] = useState(on.change.length > 0);
  const plan = next ? on : off;
  const n = plan.change.length;
  const isRequired = flag === "alwaysRequired";

  const choice = (value: boolean, count: number) => (
    <label
      className={`flex items-center gap-2 ${count === 0 ? "text-slate-400" : ""}`}
    >
      <input
        type="radio"
        name="folder-set-all"
        checked={next === value}
        disabled={count === 0}
        onChange={() => setNext(value)}
      />
      {value ? "Turn on" : "Turn off"}{" "}
      {count === 0 ? "(nothing to change)" : `(${count} change)`}
    </label>
  );

  const confirmLabel = isRequired
    ? next
      ? `Make ${n} required`
      : `Make ${n} not required`
    : `Set Per store ${next ? "on" : "off"} ${n}`;

  const summary = isRequired
    ? next
      ? `${n} leaves will become required. ${leaves.length - n} already are.`
      : `${n} leaves will stop being required.`
    : `${n} leaves will change. ${leaves.length - n} already match.`;

  return (
    <ConfirmationModal
      open
      onOpenChange={(open) => !open && onClose()}
      variant="primary"
      title={`${FLAG_NAME[flag]} for all ${leaves.length} leaves in ${folder}`}
      confirmLabel={confirmLabel}
      onConfirm={() => {
        if (n > 0) onConfirm(plan.change, flag, next);
        onClose();
      }}
      description={
        <span className="block space-y-2">
          <span
            className="flex gap-4"
            role="radiogroup"
            aria-label="Turn on or off"
          >
            {choice(true, on.change.length)}
            {choice(false, off.change.length)}
          </span>
          <span className="block">{summary}</span>
          {isRequired && next && plan.live > 0 && (
            <span className="block">
              {plan.live} of these {n} are on live seller forms. Sellers with
              drafts get a &quot;now required&quot; note.
            </span>
          )}
          <span className="block">
            Saves one leaf at a time. Each row shows its own mark.
          </span>
        </span>
      }
    />
  );
}

export function FolderSetAllConfirm<T extends Flagged>({
  target,
  ...rest
}: FolderSetAllConfirmProps<T>) {
  return target ? (
    <Body key={`${target.folder}|${target.flag}`} target={target} {...rest} />
  ) : null;
}
